import { useState } from 'react';
import type { JigStep } from '../../protocol/types.ts';
import { buildClicker, validateClicker, type ClickerSettings } from './clicker.ts';

export function Clicker({ onUse, compact = false }: { onUse: (steps: JigStep[], durationMs: number) => void; compact?: boolean }) {
  const [settings, setSettings] = useState<ClickerSettings>({ button: 1, intervalMs: 1000, holdMs: 50, durationS: 60 });
  const errors = validateClicker(settings);
  return (
    <section className="panel">
      <h2>{compact ? 'Auto-click' : 'Auto-clicker'}</h2>
      <p className="dim">{compact ? 'Clicks at the current pointer position' : 'Creates a two-step macro. Clicks repeat at the current cursor position for the run duration. Creating a draft does not start the device.'}</p>
      <label className="field__label">
        <span>Mouse button</span>
        <select className="text" value={settings.button} onChange={(e) => setSettings({ ...settings, button: Number(e.target.value) })}>
          <option value={1}>Left</option>
          <option value={2}>Right</option>
          <option value={4}>Middle</option>
        </select>
      </label>
      {([
        ['intervalMs', 'Click interval (ms)', 4, 65535],
        ['holdMs', 'Hold time (ms)', 2, 65533],
        ['durationS', 'Run duration (s)', 0, 65535],
      ] as const).map(([key, label, min, max]) => (
        <div className="field" key={key}>
          <label className="field__label">
            <span>{label}</span>
            <input className="num" type="number" min={min} max={max} step={1}
              value={Number.isNaN(settings[key]) ? '' : settings[key]}
              onChange={(e) => setSettings({ ...settings, [key]: e.target.valueAsNumber })} />
          </label>
        </div>
      ))}
      <p className="dim">{compact ? '0 = until stopped' : 'Interval includes hold time. Duration 0 repeats until stopped or unplugged; finite durations may end mid-click and the firmware releases the button.'}</p>
      <p className="warn">{compact ? 'Saved clicks auto-start on plug-in. They can activate anything under the pointer. Stop here or unplug.' : 'Saved clicking macros auto-start after the boot delay on every power-up. They can activate whatever is under the cursor, even with zero movement. Stop in this page or unplug to interrupt; replugging starts the saved macro again.'}</p>
      {errors.length > 0 && <ul className="problems">{errors.map((error) => <li key={error}>{error}</li>)}</ul>}
      <button type="button" className="btn btn--primary" disabled={errors.length > 0}
        onClick={() => onUse(buildClicker(settings), settings.durationS * 1000)}>
        {compact ? 'Use auto-click' : 'Use clicker as pattern'}
      </button>
    </section>
  );
}
